<script setup>
import { computed, ref } from 'vue'
import { Head, Link, router } from '@inertiajs/vue3'
import { ArrowLeft, AlertTriangle } from 'lucide-vue-next'
import AdminLayout from '../../Layout/AdminLayout.vue'
import RecordForm from '../../Components/Admin/RecordForm.vue'
import { PART_CALLOUTS } from '../../lib/aircraftMap'
import { brl } from '../../lib/format'

defineOptions({ layout: AdminLayout })

const props = defineProps({
  aircraft: { type: Object, required: true },
  client: { type: Object, required: true },
  order: { type: Object, required: true },
  steps: { type: Array, required: true },
  tasks: { type: Array, required: true },
  components: { type: Array, required: true },
  installments: { type: Array, required: true },
  history: { type: Array, required: true },
  totals: { type: Object, required: true },
})

const aba = ref('geral')
const abas = [
  { id: 'geral', label: 'Geral' },
  { id: 'etapas', label: 'Etapas' },
  { id: 'tarefas', label: 'Tarefas' },
  { id: 'componentes', label: 'Componentes' },
  { id: 'parcelas', label: 'Parcelas' },
  { id: 'historico', label: 'Histórico' },
]

// Estados válidos: espelham STATUS e PAYMENT_STATUS em lib/status.js.
const ESTADO_ETAPA = [
  { value: 'done', label: 'Concluída' },
  { value: 'active', label: 'Em execução' },
  { value: 'pending', label: 'Pendente' },
]
const ESTADO_ITEM = [
  { value: 'done', label: 'Concluído' },
  { value: 'in_progress', label: 'Em andamento' },
  { value: 'pending', label: 'Pendente' },
]
const ESTADO_PARCELA = [
  { value: 'paid', label: 'Paga' },
  { value: 'due', label: 'A vencer' },
  { value: 'overdue', label: 'Vencida' },
  { value: 'pending', label: 'Programada' },
  { value: 'waiting', label: 'Aguardando conclusão' },
]

// As peças vêm de PART_CALLOUTS, que guarda as coordenadas no diagrama: é a
// mesma fonte que o mapa usa, então não há como cadastrar peça sem lugar.
const PECAS = Object.keys(PART_CALLOUTS).map((p) => ({ value: p, label: p }))
const noMapa = (part) => part in PART_CALLOUTS

const camposGerais = computed(() => [
  { name: 'number', label: 'Ordem de serviço', required: true, span: 'sm:col-span-3', placeholder: 'OS 0248' },
  { name: 'status', label: 'Status', required: true, span: 'sm:col-span-3', placeholder: 'Em manutenção' },
  { name: 'technician', label: 'Responsável técnico', span: 'sm:col-span-4' },
  { name: 'opened_on', label: 'Abertura', type: 'date', span: 'sm:col-span-3' },
  { name: 'delivery_forecast', label: 'Previsão de entrega', type: 'date', span: 'sm:col-span-3' },
  {
    name: 'progress',
    label: 'Progresso (%)',
    type: 'number',
    min: 0,
    max: 100,
    span: 'sm:col-span-4',
    hint: `As etapas sugerem ${props.order.suggestedProgress}%.`,
  },
])

const camposEtapa = [
  { name: 'label', label: 'Etapa', required: true, span: 'sm:col-span-3' },
  { name: 'state', label: 'Estado', type: 'select', options: ESTADO_ETAPA, required: true, span: 'sm:col-span-2' },
  { name: 'occurred_on', label: 'Data', type: 'date', span: 'sm:col-span-2', hint: 'Pendente: data prevista.' },
  { name: 'detail', label: 'Descrição', span: 'sm:col-span-3' },
]

const camposTarefa = [
  { name: 'label', label: 'Tarefa', required: true, span: 'sm:col-span-3' },
  { name: 'stage', label: 'Etapa', required: true, span: 'sm:col-span-2', placeholder: 'Execução' },
  { name: 'state', label: 'Estado', type: 'select', options: ESTADO_ITEM, required: true, span: 'sm:col-span-2' },
  { name: 'reported_on', label: 'Atualizada em', type: 'date', span: 'sm:col-span-3' },
  { name: 'technician', label: 'Responsável', span: 'sm:col-span-4' },
  { name: 'note', label: 'Nota técnica', type: 'textarea', span: 'sm:col-span-6' },
]

const camposComponente = [
  { name: 'part', label: 'Peça', type: 'select', options: PECAS, required: true, span: 'sm:col-span-3' },
  { name: 'state', label: 'Estado', type: 'select', options: ESTADO_ITEM, required: true, span: 'sm:col-span-2' },
  { name: 'reported_on', label: 'Atualizado em', type: 'date', span: 'sm:col-span-2' },
  { name: 'technician', label: 'Responsável', span: 'sm:col-span-3' },
  { name: 'summary', label: 'Resumo', type: 'textarea', span: 'sm:col-span-10' },
]

const camposTarefaComponente = [
  { name: 'label', label: 'Tarefa da peça', required: true, span: 'sm:col-span-6' },
  { name: 'state', label: 'Estado', type: 'select', options: ESTADO_ITEM, required: true, span: 'sm:col-span-4' },
]

const camposParcela = [
  { name: 'label', label: 'Parcela', required: true, span: 'sm:col-span-3' },
  { name: 'amount', label: 'Valor (R$)', type: 'number', step: '0.01', min: 0, required: true, span: 'sm:col-span-2' },
  { name: 'state', label: 'Estado', type: 'select', options: ESTADO_PARCELA, required: true, span: 'sm:col-span-2' },
  { name: 'due_on', label: 'Vencimento', type: 'date', span: 'sm:col-span-3' },
  { name: 'due_label', label: 'Vencimento sem data', span: 'sm:col-span-2', hint: 'Ex.: "na entrega".' },
  { name: 'settled_on', label: 'Compensada em', type: 'date', span: 'sm:col-span-2' },
  { name: 'short_label', label: 'Rótulo curto', span: 'sm:col-span-2', hint: 'Card da Visão Geral.' },
  { name: 'share', label: 'Percentual', span: 'sm:col-span-2', hint: 'Ex.: "30% do contrato".' },
  { name: 'note', label: 'Observação', type: 'textarea', span: 'sm:col-span-8' },
  { name: 'highlighted', label: 'Destaque', type: 'checkbox', checkboxLabel: 'Mostrar na Visão Geral', span: 'sm:col-span-2' },
]

const camposHistorico = [
  { name: 'scope', label: 'Onde aparece', type: 'select', required: true, span: 'sm:col-span-2',
    options: [{ value: 'maintenance', label: 'Manutenção' }, { value: 'payments', label: 'Pagamentos' }] },
  { name: 'author', label: 'Autor', required: true, span: 'sm:col-span-2', placeholder: 'Equipe UP' },
  { name: 'occurred_at', label: 'Data e hora', type: 'datetime-local', required: true, span: 'sm:col-span-3' },
  { name: 'text', label: 'Texto', type: 'textarea', required: true, span: 'sm:col-span-10' },
]

const removerOrdem = () => {
  if (confirm(`Remover ${props.order.number} e tudo que pende dela?`)) {
    router.delete(`/admin/ordens/${props.order.id}`)
  }
}

const url = {
  order: `/admin/ordens/${props.order.id}`,
  steps: `/admin/ordens/${props.order.id}/etapas`,
  tasks: `/admin/ordens/${props.order.id}/tarefas`,
  components: `/admin/ordens/${props.order.id}/componentes`,
  installments: `/admin/ordens/${props.order.id}/parcelas`,
  history: `/admin/ordens/${props.order.id}/historico`,
}
</script>

<template>
  <Head :title="`${props.order.number} · Administração`" />

  <Link :href="`/admin/aeronaves/${props.aircraft.id}`" class="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-up-navy">
    <ArrowLeft class="h-4 w-4" aria-hidden="true" />
    {{ props.aircraft.registration }} · {{ props.client.name }}
  </Link>

  <h1 class="mt-3 text-xl font-semibold tracking-tight text-up-navy">{{ props.order.number }}</h1>
  <p class="mt-1 text-sm text-slate-500">
    {{ props.aircraft.registration }} · {{ props.aircraft.model }} · {{ props.order.status }}
  </p>

  <nav class="mt-6 flex flex-wrap gap-1 border-b border-slate-200" aria-label="Seções da ordem de serviço">
    <button
      v-for="t in abas"
      :key="t.id"
      type="button"
      class="-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors"
      :class="aba === t.id ? 'border-up-orange text-up-orange' : 'border-transparent text-slate-500 hover:text-up-navy'"
      :aria-current="aba === t.id ? 'true' : undefined"
      @click="aba = t.id"
    >
      {{ t.label }}
    </button>
  </nav>

  <!-- ===== Geral ===== -->
  <section v-if="aba === 'geral'" class="mt-6 space-y-6">
    <div class="rounded-card bg-white p-6 shadow-card">
      <h2 class="mb-4 text-sm font-semibold tracking-wide text-up-navy uppercase">Dados da ordem de serviço</h2>
      <RecordForm :fields="camposGerais" :record="props.order" param-key="service_order" :action="url.order" />
    </div>

    <div class="rounded-card bg-white p-6 shadow-card">
      <h2 class="text-sm font-semibold tracking-wide text-up-navy uppercase">Remover ordem de serviço</h2>
      <p class="mt-1 text-sm text-slate-500">
        Apaga etapas, tarefas, componentes, parcelas, histórico e as fotos do serviço.
        O portal do cliente passa a mostrar a tela de espera.
      </p>
      <button
        type="button"
        class="mt-4 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:border-up-orange hover:text-up-orange"
        @click="removerOrdem"
      >
        Remover ordem de serviço
      </button>
    </div>
  </section>

  <!-- ===== Etapas ===== -->
  <section v-else-if="aba === 'etapas'" class="mt-6 space-y-3">
    <p class="text-sm text-slate-500">
      As cinco etapas alimentam o stepper do cliente. A etapa "Em execução" é a que aparece em destaque.
    </p>
    <div v-for="e in props.steps" :key="e.id" class="rounded-card bg-white p-5 shadow-card">
      <RecordForm
        :fields="camposEtapa"
        :record="e"
        param-key="maintenance_step"
        :action="`/admin/etapas/${e.id}`"
        :delete-url="`/admin/etapas/${e.id}`"
        :delete-confirm="`Remover a etapa ${e.label}?`"
      />
    </div>
    <div class="rounded-card border border-dashed border-slate-300 bg-white p-5">
      <RecordForm :fields="camposEtapa" param-key="maintenance_step" :action="url.steps" />
    </div>
  </section>

  <!-- ===== Tarefas ===== -->
  <section v-else-if="aba === 'tarefas'" class="mt-6 space-y-3">
    <p class="text-sm text-slate-500">
      Tarefas são agrupadas por etapa na tela do cliente. Sem data, a tela mostra "—".
    </p>
    <div v-for="t in props.tasks" :key="t.id" class="rounded-card bg-white p-5 shadow-card">
      <RecordForm
        :fields="camposTarefa"
        :record="t"
        param-key="maintenance_task"
        :action="`/admin/tarefas/${t.id}`"
        :delete-url="`/admin/tarefas/${t.id}`"
        :delete-confirm="`Remover a tarefa ${t.label}?`"
      />
    </div>
    <div class="rounded-card border border-dashed border-slate-300 bg-white p-5">
      <RecordForm :fields="camposTarefa" param-key="maintenance_task" :action="url.tasks" />
    </div>
  </section>

  <!-- ===== Componentes ===== -->
  <section v-else-if="aba === 'componentes'" class="mt-6 space-y-3">
    <p class="text-sm text-slate-500">
      Cada peça tem um ponto no Mapa da Aeronave. A lista de peças é a mesma que o diagrama conhece.
    </p>
    <div v-for="c in props.components" :key="c.id" class="rounded-card bg-white p-5 shadow-card">
      <p v-if="!noMapa(c.part)" class="mb-3 flex items-center gap-2 rounded-lg bg-up-orange/10 px-3 py-2 text-xs text-up-orange-dark">
        <AlertTriangle class="h-4 w-4 shrink-0" aria-hidden="true" />
        "{{ c.part }}" não tem posição no diagrama: aparece na lista do cliente, mas não no desenho da aeronave.
      </p>
      <RecordForm
        :fields="camposComponente"
        :record="c"
        param-key="component"
        :action="`/admin/componentes/${c.id}`"
        :delete-url="`/admin/componentes/${c.id}`"
        :delete-confirm="`Remover o componente ${c.part}?`"
      />

      <div class="mt-4 border-t border-slate-100 pt-4">
        <p class="mb-2 text-xs font-semibold tracking-wide text-slate-500 uppercase">Tarefas da peça</p>
        <div v-for="ct in c.tasks" :key="ct.id" class="mb-2">
          <RecordForm
            :fields="camposTarefaComponente"
            :record="ct"
            param-key="component_task"
            :action="`/admin/tarefas-de-componente/${ct.id}`"
            :delete-url="`/admin/tarefas-de-componente/${ct.id}`"
            :delete-confirm="`Remover ${ct.label}?`"
          />
        </div>
        <RecordForm
          :fields="camposTarefaComponente"
          param-key="component_task"
          :action="`/admin/componentes/${c.id}/tarefas-de-componente`"
        />
      </div>
    </div>
    <div class="rounded-card border border-dashed border-slate-300 bg-white p-5">
      <RecordForm :fields="camposComponente" param-key="component" :action="url.components" />
    </div>
  </section>

  <!-- ===== Parcelas ===== -->
  <section v-else-if="aba === 'parcelas'" class="mt-6 space-y-3">
    <div class="rounded-card bg-white p-5 shadow-card">
      <dl class="grid grid-cols-3 gap-4 text-sm">
        <div><dt class="text-slate-500">Total</dt><dd class="mt-0.5 font-semibold text-up-navy">{{ brl(props.totals.total) }}</dd></div>
        <div><dt class="text-slate-500">Pago</dt><dd class="mt-0.5 font-semibold text-up-green">{{ brl(props.totals.paid) }}</dd></div>
        <div><dt class="text-slate-500">Em aberto</dt><dd class="mt-0.5 font-semibold text-up-navy">{{ brl(props.totals.outstanding) }}</dd></div>
      </dl>
      <!-- Os totais são somados das parcelas, nunca digitados. -->
      <p class="mt-3 text-xs text-slate-400">Calculados a partir das parcelas abaixo; não há campo de total.</p>
    </div>

    <div v-for="i in props.installments" :key="i.id" class="rounded-card bg-white p-5 shadow-card">
      <RecordForm
        :fields="camposParcela"
        :record="i"
        param-key="installment"
        :action="`/admin/parcelas/${i.id}`"
        :delete-url="`/admin/parcelas/${i.id}`"
        :delete-confirm="`Remover a parcela ${i.label}?`"
      />
    </div>
    <div class="rounded-card border border-dashed border-slate-300 bg-white p-5">
      <RecordForm :fields="camposParcela" param-key="installment" :action="url.installments" />
    </div>
  </section>

  <!-- ===== Histórico ===== -->
  <section v-else class="mt-6 space-y-3">
    <p class="text-sm text-slate-500">
      O feed do cliente. "Manutenção" aparece em /manutencao; "Pagamentos", em /pagamentos.
    </p>
    <div v-for="h in props.history" :key="h.id" class="rounded-card bg-white p-5 shadow-card">
      <RecordForm
        :fields="camposHistorico"
        :record="h"
        param-key="history_entry"
        :action="`/admin/historico/${h.id}`"
        :delete-url="`/admin/historico/${h.id}`"
        delete-confirm="Remover esta atualização?"
      />
    </div>
    <div class="rounded-card border border-dashed border-slate-300 bg-white p-5">
      <RecordForm :fields="camposHistorico" param-key="history_entry" :action="url.history" />
    </div>
  </section>
</template>
